import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import logo from "./assets/logo.png";
import { useAuth, useAuthUpdate } from "contexts/AuthContext";

// Import icons from lucide-react
import {
  Home,
  BarChart3,
  Users,
  BookOpen,
  FileText,
  HelpCircle,
  CheckSquare,
  FilePlus,
  Globe,
  Target,
  Speaker,
  Search,
  Folder,
  Settings,
  CheckCircle,
  Layers,
  LogOut,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Archive,
  FileBox,
  Calendar,
  ClipboardCheck,
} from "lucide-react";
import {
  STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS,
  REGISTRAL_ROLE_CONDUCT_RESEARCH_TABS,
  OAGB_MANAGEMENT_HEAD_ROLE_CONDUCT_RESEARCH_TABS,
  LEGAL_RESEARCH_PROSECUTOR_ROLE_CONDUCT_RESEARCH_TABS,
  LEGAL_RESEARCH_COORDINATOR_ROLE_CONDUCT_RESEARCH_TABS,
} from "utils/constantValues";

type SideBarPropes = {
  isInternalUser: boolean;
};

// Map tab labels to icons
const TAB_ICONS: Record<string, React.ReactNode> = {
  "External Request": <FileBox className="w-4 h-4" />,
  Archived: <Archive className="w-4 h-4" />,
  "New Request": <FilePlus className="w-4 h-4" />,
  "Legal Research Request": <Search className="w-4 h-4" />,
  "Committee Request": <Users className="w-4 h-4" />,
  "Internal Request": <Folder className="w-4 h-4" />,
  "Internal Request Topics": <Folder className="w-4 h-4" />,
  "Proposal Submission": <ClipboardCheck className="w-4 h-4" />,
  "Scheduled Presentation": <Calendar className="w-4 h-4" />,
};

// Helper to convert tab label to path
const tabToPath = (tab: string): string => {
  const pathMap: Record<string, string> = {
    "External Request": "/conduct-legal-research/external",
    Archived: "/conduct-legal-research/archived",
    "New Request": "/conduct-legal-research",
    "Legal Research Request": "/conduct-legal-research/legal-research",
    "Committee Request": "/conduct-legal-research/committee",
    "Internal Request": "/conduct-legal-research/internal",
    "Internal Request Topics": "/conduct-legal-research/internal-topics",
    "Proposal Submission": "/conduct-legal-research/proposal-submission",
    "Scheduled Presentation": "/conduct-legal-research/scheduled",
  };
  return (
    pathMap[tab] ||
    `/conduct-legal-research/${tab.toLowerCase().replace(/\s+/g, "-")}`
  );
};

const Sidebar: React.FC<SideBarPropes> = ({ isInternalUser }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const contextData = useAuth();
  const navigate = useNavigate();
  const updateAuthContext = useAuthUpdate();

  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >({
    "conduct-legal-research": location.pathname.includes(
      "/conduct-legal-research"
    ),
  });

  const handleLogout = () => {
    sessionStorage.removeItem("authToken");
    updateAuthContext({
      ...contextData,
      token: "",
    });
    window.location.href = "/login";
  };

  // Get tabs based on user role
  const getConductLegalResearchTabs = () => {
    if (!contextData.user?.roles) {
      return STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS;
    }

    if (contextData.user.roles.includes("registrar")) {
      return REGISTRAL_ROLE_CONDUCT_RESEARCH_TABS;
    } else if (contextData.user.roles.includes("OAGB Management Head")) {
      return OAGB_MANAGEMENT_HEAD_ROLE_CONDUCT_RESEARCH_TABS;
    } else if (contextData.user.roles.includes("Legal Research Prosecutor")) {
      return LEGAL_RESEARCH_PROSECUTOR_ROLE_CONDUCT_RESEARCH_TABS;
    } else if (
      [
        "LegalResearchCoordinator",
        "attorneygeneral",
        "LRDIFUDeputy",
        "OAGBManagementHead",
        "LRDIFUDDirector",
        "LegalResearchProsecutor",
        "GeneralAttorney",
      ].some((r) => contextData.user.roles.includes(r))
    ) {
      return LEGAL_RESEARCH_COORDINATOR_ROLE_CONDUCT_RESEARCH_TABS;
    }

    return STAKEHOLDER_ROLE_CONDUCT_RESEARCH_TABS;
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId],
    }));
  };

  const navItem = (title: string, path: string, icon: React.ReactNode) => {
    const isActive =
      path === "/"
        ? location.pathname === "/"
        : location.pathname === path ||
          (path !== "/" &&
            location.pathname.startsWith(path) &&
            !location.pathname.includes("/conduct-legal-research/") &&
            path !== "/conduct-legal-research");

    return (
      <Link to={path}>
        <div
          className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 group ${
            isActive
              ? "bg-white text-black shadow-md"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg opacity-90 group-hover:opacity-100">
              {icon}
            </span>
            <span className="text-sm">{t(title)}</span>
          </div>
          {isActive && <ChevronRight className="w-4 h-4" />}
        </div>
      </Link>
    );
  };

  const subNavItem = (title: string, isActive: boolean) => {
    return (
      <Link
        to="/conduct-legal-research"
        state={{ activeTab: title }}
        replace={true}
      >
        <div
          className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
            isActive
              ? "bg-gray-800 text-white border-white"
              : "hover:bg-gray-800 hover:text-white border-transparent hover:border-gray-600"
          }`}
        >
          <span className="opacity-70">
            {TAB_ICONS[title] || <ChevronRight className="w-3 h-3" />}
          </span>
          <span className="text-xs">{t(title)}</span>
        </div>
      </Link>
    );
  };

  const ConductLegalResearchDropdown = () => {
    const tabs = getConductLegalResearchTabs();
    const isParentActive = location.pathname.includes(
      "/conduct-legal-research"
    );
    const isExpanded =
      expandedSections["conduct-legal-research"] || isParentActive;

    const currentTab = location.state?.activeTab;

    return (
      <div className="mb-1">
        <div
          onClick={() => toggleSection("conduct-legal-research")}
          className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 cursor-pointer ${
            isParentActive
              ? "bg-gray-800 text-white"
              : "hover:bg-gray-800 hover:text-white"
          }`}
        >
          <div className="flex items-center gap-3">
            <span className="text-lg">
              <Search className="w-5 h-5" />
            </span>
            <span className="text-sm">{t("ConductLegalResearch")}</span>
          </div>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </div>

        {isExpanded && (
          <div className="mt-1 ml-2 space-y-0.5">
            {tabs.map((tab) => {
              const isSubActive = currentTab === tab;
              return <div key={tab}>{subNavItem(tab, isSubActive)}</div>;
            })}
          </div>
        )}
      </div>
    );
  };

  const handleTabChange = (tabName: string) => {
    navigate("/conduct-legal-research", {
      state: { activeTab: tabName },
      replace: true,
    });
  };

  // Logout button component
  const logoutButton = () => (
    <button
      onClick={handleLogout}
      className="flex items-center gap-3 px-3 py-2.5 rounded font-medium text-red-400 hover:bg-red-900/20 hover:text-red-300 w-full transition-colors mt-6 border-t border-gray-800 pt-4"
    >
      <LogOut className="text-lg" />
      <span>{t("Logout")}</span>
    </button>
  );

  const isZonalLRD = contextData?.user?.roles?.includes("ZonalLRD");
  const isZonalLIF = contextData?.user?.roles?.includes("ZonalLIF");
  const isZonalOfficeOfCouncilHead = contextData?.user?.roles?.includes("ZonalOfficeOfCouncilHead");
  const isWoredaLDR = contextData?.user?.roles?.includes("WoredaLDR");
  const isLATCoordinator = contextData?.user?.roles?.includes("LATCoordinator");
  const isLegalResearchCoordinator = contextData?.user?.roles?.includes("LegalResearchCoordinator");
  const isLegalTranslationCordinator = contextData?.user?.roles?.includes("LegalTranslationCordinator");
  const isImpAwarenessCordinator = contextData?.user?.roles?.includes("ImpAwarenessCordinator");
  const isLegalResearchProsecutor = contextData?.user?.roles?.includes("LegalResearchProsecutor");
  const isLegalTranslationProsecutor = contextData?.user?.roles?.includes("LegalTranslationProsecutor");
  const isImpAwarenessProsecutor = contextData?.user?.roles?.includes("ImpAwarenessProsecutor");
  const isLRDIFUDDirector = contextData?.user?.roles?.includes("LRDIFUDDirector");
  const rolesCount = contextData?.user?.roles?.length || 0;

  // If user has ImpAwarenessCordinator role - show Law Implementation and Legal Awareness Creation
  if (isImpAwarenessCordinator) {
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img src={logo} alt={t("Logo")} className="w-10 h-10 rounded-lg shadow-md" />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              {navItem(t("LegalAwarenessCreation"), "/legal-awareness-creation", <Speaker className="w-5 h-5" />)}
              {navItem(t("LawImplementation"), "/law-implementation", <BookOpen className="w-5 h-5" />)}
            </Section>
          </nav>
        </div>
      </aside>
    );
  }


  // LRDIFUDDirector role - can view all sidebar tabs (full internal user layout)
  if (isLRDIFUDDirector) {
    const isTranslationExpanded = expandedSections["legal-translation"] || false;
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar flex flex-col">
        <div className="p-4 flex-grow">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img
              src={logo}
              alt={t("Logo")}
              className="w-10 h-10 rounded-lg shadow-md"
            />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>

          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              <ConductLegalResearchDropdown />

              {navItem(
                t("LegislativeDrafting"),
                "/legislative-drafting",
                <FileText className="w-5 h-5" />
              )}
              {navItem(
                t("LegalAdvisory"),
                "/legal-advisory",
                <HelpCircle className="w-5 h-5" />
              )}
              {navItem(
                t("CheckingAccuracyOfSectors"),
                "/sector-directives",
                <CheckCircle className="w-5 h-5" />
              )}
              {navItem(
                t("RegisteringDirectives"),
                "/registering-directives",
                <FilePlus className="w-5 h-5" />
              )}
              <div className="mb-1">
                <div
                  onClick={() => toggleSection("legal-translation")}
                  className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 cursor-pointer ${
                    location.pathname.includes("/legal-translation")
                      ? "bg-gray-800 text-white"
                      : "hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      <Globe className="w-5 h-5" />
                    </span>
                    <span className="text-sm">{t("LegalTranslation")}</span>
                  </div>
                  {expandedSections["legal-translation"] ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>

                {expandedSections["legal-translation"] && (
                  <div className="mt-1 ml-2 space-y-0.5">
                    <Link to="/legal-translation/external">
                      <div
                        className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                          (location.pathname.startsWith("/legal-translation") && !location.pathname.includes("/legal-translation/internal"))
                            ? "bg-gray-800 text-white border-white"
                            : "hover:bg-gray-800 hover:text-white border-transparent hover:border-gray-600"
                        }`}
                      >
                        <span className="opacity-70">
                          <ChevronRight className="w-3 h-3" />
                        </span>
                        <span className="text-xs">{t("External")}</span>
                      </div>
                    </Link>
                    <Link to="/legal-translation/internal">
                      <div
                        className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                          location.pathname.includes("/legal-translation/internal")
                            ? "bg-gray-800 text-white border-white"
                            : "hover:bg-gray-800 hover:text-white border-transparent hover:border-gray-600"
                        }`}
                      >
                        <span className="opacity-70">
                          <ChevronRight className="w-3 h-3" />
                        </span>
                        <span className="text-xs">{t("Internal")}</span>
                      </div>
                    </Link>
                  </div>
                )}
              </div>
              {navItem(
                t("LawImplementation"),
                "/law-implementation",
                <CheckSquare className="w-5 h-5" />
              )}
              {navItem(
                t("LegalAwarenessCreation"),
                "/legal-awareness-creation",
                <Speaker className="w-5 h-5" />
              )}
              {navItem(
                t("LegalTrainingProvision"),
                "/legal-training-provision",
                <BookOpen className="w-5 h-5" />
              )}
            </Section>
          </nav>
        </div>
        <div className="p-4 text-xs text-gray-500 border-t border-gray-800 bg-gray-900/50">
          <div className="flex items-center justify-between">
            <span>{t("PoweredBy")}</span>
            <Settings className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer" />
          </div>
        </div>
      </aside>
    );
  }

  // If user has LegalTranslationCordinator role - show Legal Translation tab only
  if (isLegalTranslationCordinator) {
    const isTranslationExpanded = expandedSections["legal-translation"] || false;
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img src={logo} alt={t("Logo")} className="w-10 h-10 rounded-lg shadow-md" />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              <div className="mb-1">
                <div
                  onClick={() => {
                    setExpandedSections((prev) => ({
                      ...prev,
                      "legal-translation": !prev["legal-translation"],
                    }));
                  }}
                  className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 cursor-pointer ${
                    location.pathname.includes("/legal-translation")
                      ? "bg-gray-800 text-white"
                      : "hover:bg-gray-800 hover:text-white"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-lg">
                      <Globe className="w-5 h-5" />
                    </span>
                    <span className="text-sm">{t("LegalTranslation")}</span>
                  </div>
                  {isTranslationExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </div>

                {isTranslationExpanded && (
                  <div className="mt-1 ml-2 space-y-0.5">
                    <div>
                      <Link to="/legal-translation/external" state={{ external: true }} replace={true}>
                        <div
                          className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                            (location.pathname.startsWith("/legal-translation") && !location.pathname.includes("/legal-translation/internal"))
                              ? "bg-gray-800 text-white border-white"
                              : "border-transparent hover:bg-gray-800 hover:text-white hover:border-gray-600"
                          }`}
                        >
                          <span className="opacity-70">
                            <Globe className="w-3 h-3" />
                          </span>
                          <span className="text-xs">{t("External")}</span>
                        </div>
                      </Link>
                    </div>
                    <div>
                      <Link to="/legal-translation/internal" state={{ external: false }} replace={true}>
                        <div
                          className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                            location.pathname.includes("/legal-translation/internal")
                              ? "bg-gray-800 text-white border-white"
                              : "border-transparent hover:bg-gray-800 hover:text-white hover:border-gray-600"
                          }`}
                        >
                          <span className="opacity-70">
                            <Globe className="w-3 h-3" />
                          </span>
                          <span className="text-xs">{t("Internal")}</span>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </Section>
          </nav>
        </div>
      </aside>
    );
  }

  // If the user has ONLY the LegalResearchCoordinator role
  if (isLegalResearchCoordinator) {
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img
              src={logo}
              alt={t("Logo")}
              className="w-10 h-10 rounded-lg shadow-md"
            />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              <ConductLegalResearchDropdown />
              {navItem(
                t("LegislativeDrafting"),
                "/legislative-drafting",
                <FileText className="w-5 h-5" />
              )}
              {navItem(
                t("LegalAdvisory"),
                "/legal-advisory",
                <HelpCircle className="w-5 h-5" />
              )}
              {navItem(
                t("CheckingAccuracyOfSectors"),
                "/sector-directives",
                <CheckCircle className="w-5 h-5" />
              )}
              {navItem(
                t("RegisteringDirectives"),
                "/registering-directives",
                <FilePlus className="w-5 h-5" />
              )}
              {navItem(
                t("LegalTrainingProvision"),
                "/legal-training-provision",
                <BookOpen className="w-5 h-5" />
              )}
            </Section>
          </nav>
        </div>
      </aside>
    );
  }

  // Prosecutors: one menu built from every prosecutor role the user holds, so a
  // user with several (research, awareness / follow-up, translation) reaches the
  // work of each. A single role shows the same menu as before.
  if (isLegalResearchProsecutor || isImpAwarenessProsecutor || isLegalTranslationProsecutor) {
    const isTranslationExpanded = expandedSections["legal-translation"] || false;
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img src={logo} alt={t("Logo")} className="w-10 h-10 rounded-lg shadow-md" />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              {isLegalResearchProsecutor && (
                <>
                  <ConductLegalResearchDropdown />
                  {navItem(t("LegislativeDrafting"), "/legislative-drafting", <FileText className="w-5 h-5" />)}
                  {navItem(t("LegalTrainingProvision"), "/legal-training-provision", <BookOpen className="w-5 h-5" />)}
                  {navItem(t("CheckingAccuracyOfSectors"), "/sector-directives", <CheckCircle className="w-5 h-5" />)}
                  {navItem(t("RegisteringDirectives"), "/registering-directives", <FilePlus className="w-5 h-5" />)}
                  {navItem(t("LegalAdvisory"), "/legal-advisory", <HelpCircle className="w-5 h-5" />)}
                </>
              )}
              {isLegalTranslationProsecutor && (
                <div className="mb-1">
                  <div
                    onClick={() => {
                      setExpandedSections((prev) => ({
                        ...prev,
                        "legal-translation": !prev["legal-translation"],
                      }));
                    }}
                    className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 cursor-pointer ${
                      location.pathname.includes("/legal-translation")
                        ? "bg-gray-800 text-white"
                        : "hover:bg-gray-800 hover:text-white"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-lg">
                        <Globe className="w-5 h-5" />
                      </span>
                      <span className="text-sm">{t("LegalTranslation")}</span>
                    </div>
                    {isTranslationExpanded ? (
                      <ChevronUp className="w-4 h-4" />
                    ) : (
                      <ChevronDown className="w-4 h-4" />
                    )}
                  </div>

                  {isTranslationExpanded && (
                    <div className="mt-1 ml-2 space-y-0.5">
                      <div>
                        <Link to="/legal-translation/external" state={{ external: true }} replace={true}>
                          <div
                            className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                              (location.pathname.startsWith("/legal-translation") && !location.pathname.includes("/legal-translation/internal"))
                                ? "bg-gray-800 text-white border-white"
                                : "border-transparent hover:bg-gray-800 hover:text-white hover:border-gray-600"
                            }`}
                          >
                            <span className="opacity-70">
                              <Globe className="w-3 h-3" />
                            </span>
                            <span className="text-xs">{t("External")}</span>
                          </div>
                        </Link>
                      </div>
                      <div>
                        <Link to="/legal-translation/internal" state={{ external: false }} replace={true}>
                          <div
                            className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                              location.pathname.includes("/legal-translation/internal")
                                ? "bg-gray-800 text-white border-white"
                                : "border-transparent hover:bg-gray-800 hover:text-white hover:border-gray-600"
                            }`}
                          >
                            <span className="opacity-70">
                              <Globe className="w-3 h-3" />
                            </span>
                            <span className="text-xs">{t("Internal")}</span>
                          </div>
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {(isLegalResearchProsecutor || isImpAwarenessProsecutor) && (
                <>
                  {navItem(t("LegalAwarenessCreation"), "/legal-awareness-creation", <Speaker className="w-5 h-5" />)}
                  {navItem(t("LawImplementation"), "/law-implementation", <BookOpen className="w-5 h-5" />)}
                </>
              )}
            </Section>
          </nav>
        </div>
      </aside>
    );
  }
  // WoredaLDR role should only see Legal Awareness tab
  if (isWoredaLDR) {
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img
              src={logo}
              alt={t("Logo")}
              className="w-10 h-10 rounded-lg shadow-md"
            />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              {navItem(
                t("LegalAwarenessCreation"),
                "/legal-awareness-creation",
                <Speaker className="w-5 h-5" />
              )}
            </Section>
          </nav>
        </div>
      </aside>
    );
  }

  // ZonalLRD and ZonalLIF roles should only see Legal Awareness Creation and Law Implementation
  if (isZonalLRD || isZonalLIF) {
    return (
      <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
        <div className="p-4">
          <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
            <img
              src={logo}
              alt={t("Logo")}
              className="w-10 h-10 rounded-lg shadow-md"
            />
            <div className="text-xs leading-tight">
              <div className="text-sm font-bold">{t("OrganizationName")}</div>
              <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
            </div>
          </div>
          <nav className="flex flex-col space-y-1">
            <Section title={t("Dashboard")}>
              {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
              {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
            </Section>
            <Section title={t("ResearchLawDraftingFollowup")}>
              {isZonalLRD || isZonalLIF ? (
                <>
                  {navItem(
                    t("LegalAwarenessCreation"),
                    "/legal-awareness-creation-zone",
                    <Speaker className="w-5 h-5" />
                  )}
                  {navItem(
                    t("LawImplementation"),
                    "/law-implementation",
                    <CheckSquare className="w-5 h-5" />
                  )}
                  {navItem(
                    t("LegalTrainingProvision"),
                    "/legal-training-provision-zone",
                    <BookOpen className="w-5 h-5" />
                  )}
                </>
              ) : (
                <>
                  {navItem(
                    t("LegalAwarenessCreation"),
                    "/legal-awareness-creation",
                    <Speaker className="w-5 h-5" />
                  )}
                  {navItem(
                    t("LawImplementation"),
                    "/law-implementation",
                    <CheckSquare className="w-5 h-5" />
                  )}
                </>
              )}
            </Section>
          </nav>
        </div>
      </aside>
    );
  }

  return isZonalOfficeOfCouncilHead ? (
    <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar">
      <div className="p-4">
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
          <img
            src={logo}
            alt={t("Logo")}
            className="w-10 h-10 rounded-lg shadow-md"
          />
          <div className="text-xs leading-tight">
            <div className="text-sm font-bold">{t("OrganizationName")}</div>
            <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
          </div>
        </div>
        <nav className="flex flex-col space-y-1">
          <Section title={t("Dashboard")}>
            {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
            {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
          </Section>
          <Section title={t("ResearchLawDraftingFollowup")}>
            {navItem(
              t("LegalTrainingProvision"),
              "/legal-training-provision-zone",
              <BookOpen className="w-5 h-5" />
            )}
            {navItem(
              t("LegalAwarenessCreation"),
              "/legal-awareness-creation-zone",
              <Speaker className="w-5 h-5" />
            )}
          </Section>
        </nav>
      </div>
    </aside>
  ) : isInternalUser ? (
    <aside className="fixed top-0 left-0 h-full w-64 bg-gradient-to-b from-gray-900 to-black text-white z-40 overflow-y-auto sidebar-scrollbar flex flex-col">
      <div className="p-4 flex-grow">
        <div className="flex items-center gap-2 mb-6 pb-4 border-b border-gray-800">
          <img
            src={logo}
            alt={t("Logo")}
            className="w-10 h-10 rounded-lg shadow-md"
          />
          <div className="text-xs leading-tight">
            <div className="text-sm font-bold">{t("OrganizationName")}</div>
            <div className="text-gray-400 text-[11px]">{t("OrganizationNameAmharic")}</div>
          </div>
        </div>

        <nav className="flex flex-col space-y-1">
          <Section title={t("Dashboard")}>
            {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
            {navItem(t("Reports"), "/reports", <BarChart3 className="w-5 h-5" />)}
          </Section>
          <Section title={t("ResearchLawDraftingFollowup")}>
            <ConductLegalResearchDropdown />

            {navItem(
              t("LegislativeDrafting"),
              "/legislative-drafting",
              <FileText className="w-5 h-5" />
            )}
            {navItem(
              t("LegalAdvisory"),
              "/legal-advisory",
              <HelpCircle className="w-5 h-5" />
            )}
            {navItem(
              t("CheckingAccuracyOfSectors"),
              "/sector-directives",
              <CheckCircle className="w-5 h-5" />
            )}
            {navItem(
              t("RegisteringDirectives"),
              "/registering-directives",
              <FilePlus className="w-5 h-5" />
            )}
            <div className="mb-1">
              <div
                onClick={() => toggleSection("legal-translation")}
                className={`flex items-center justify-between px-3 py-2.5 rounded font-medium transition-all duration-200 cursor-pointer ${
                  location.pathname.includes("/legal-translation")
                    ? "bg-gray-800 text-white"
                    : "hover:bg-gray-800 hover:text-white"
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-lg">
                    <Globe className="w-5 h-5" />
                  </span>
                  <span className="text-sm">{t("LegalTranslation")}</span>
                </div>
                {expandedSections["legal-translation"] ? (
                  <ChevronUp className="w-4 h-4" />
                ) : (
                  <ChevronDown className="w-4 h-4" />
                )}
              </div>

              {expandedSections["legal-translation"] && (
                <div className="mt-1 ml-2 space-y-0.5">
                  <Link
                    to="/legal-translation/external"
                  >
                    <div
                      className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                        (location.pathname.startsWith("/legal-translation") && !location.pathname.includes("/legal-translation/internal"))
                          ? "bg-gray-800 text-white border-white"
                          : "hover:bg-gray-800 hover:text-white border-transparent hover:border-gray-600"
                      }`}
                    >
                      <span className="opacity-70">
                        <ChevronRight className="w-3 h-3" />
                      </span>
                      <span className="text-xs">{t("External")}</span>
                    </div>
                  </Link>
                  <Link
                    to="/legal-translation/internal"
                  >
                    <div
                      className={`flex items-center gap-2 pl-10 pr-3 py-2 rounded font-medium transition-all duration-200 ml-1 border-l-2 ${
                        location.pathname.includes("/legal-translation/internal")
                          ? "bg-gray-800 text-white border-white"
                          : "hover:bg-gray-800 hover:text-white border-transparent hover:border-gray-600"
                      }`}
                    >
                      <span className="opacity-70">
                        <ChevronRight className="w-3 h-3" />
                      </span>
                      <span className="text-xs">{t("Internal")}</span>
                    </div>
                  </Link>
                </div>
              )}
            </div>
            {navItem(
              t("LawImplementation"),
              "/law-implementation",
              <CheckSquare className="w-5 h-5" />
            )}
            {navItem(
              t("LegalAwarenessCreation"),
              "/legal-awareness-creation",
              <Speaker className="w-5 h-5" />
            )}
            {navItem(
              t("LegalTrainingProvision"),
              "/legal-training-provision",
              <BookOpen className="w-5 h-5" />
            )}
          </Section>
        </nav>
      </div>
      <div className="p-4 text-xs text-gray-500 border-t border-gray-800 bg-gray-900/50">
        <div className="flex items-center justify-between">
          <span>{t("PoweredBy")}</span>
          <Settings className="w-4 h-4 opacity-50 hover:opacity-100 cursor-pointer" />
        </div>
      </div>
    </aside>
  ) : (
    <aside className="fixed top-0 left-0 h-[100%] w-64 bg-gradient-to-b from-gray-900 to-black text-white overflow-y-auto sidebar-scrollbar z-40 shadow-xl">
      <div className="p-4">
        <nav className="flex flex-col space-y-1">
          {/* External users (stakeholders) get their own dashboard too. */}
          <Section title={t("Dashboard")}>
            {navItem(t("Dashboard"), "/", <Home className="w-5 h-5" />)}
          </Section>
          <Section title={t("ResearchLawDraftingFollowup")}>
            <ConductLegalResearchDropdown />
            {navItem(
              t("LegislativeDrafting"),
              "/legislative-drafting",
              <FileText className="w-5 h-5" />
            )}
            {navItem(
              t("LegalAdvisory"),
              "/legal-advisory",
              <HelpCircle className="w-5 h-5" />
            )}
            {navItem(
              t("CheckingAccuracyOfSectors"),
              "/sector-directives",
              <CheckCircle className="w-5 h-5" />
            )}
            {navItem(
              t("RegisteringDirectives"),
              "/registering-directives",
              <FilePlus className="w-5 h-5" />
            )}
            {navItem(
              t("LegalTranslation"),
              "/legal-translation",
              <Globe className="w-5 h-5" />
            )}
            {navItem(
              t("LegalTrainingProvision"),
              "/legal-training-provision",
              <BookOpen className="w-5 h-5" />
            )}
          </Section>
        </nav>
      </div>
    </aside>
  );
};

export default Sidebar;

// Section component
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mt-4 first:mt-0">
      <div className="mb-2 text-xs font-semibold text-gray-400 uppercase flex items-center gap-2">
        <Layers className="w-4 h-4" />
        <span>{title}</span>
      </div>
      <div className="space-y-1">{children}</div>
    </div>
  );
}